<template>
  <div class="page-container news-list-page">
    <div class="page-header">
      <div class="container">
        <h1 class="page-title">{{ $t('pages.news_list_title') }}</h1>
        <p class="breadcrumb">
          <router-link to="/">{{ $t('menu.home') }}</router-link> /
          <span>{{ $t('pages.news_list_title') }}</span>
        </p>
      </div>
    </div>

    <div class="page-content container">
      <el-row :gutter="30">
        <!-- 左侧：新闻列表 -->
        <el-col :span="18" :xs="24">
          <!-- 新增：搜索框区域 -->
          <div class="search-wrapper">
            <el-input
              v-model="searchKeyword"
              :placeholder="$t('common.enter_search_keyword')"
              :prefix-icon="Search"
              @keyup.enter="handleSearch"
              clearable
            />
            <!-- 新增按钮容器 -->
            <div class="button-row">
              <el-button type="primary" @click="handleSearch" class="search-btn">
                {{ $t('common.search') }}
              </el-button>
              <el-button
                v-if="searchKeyword"
                type="text"
                @click="resetSearch"
                class="reset-btn"
              >
                {{ $t('common.reset') }}
              </el-button>
            </div>
          </div>

          <div class="list-wrapper">
            <div v-if="loading" class="loading-placeholder">
              <el-skeleton :rows="6" animated />
            </div>
            <div v-else-if="newsList.length === 0" class="empty-data">
              <el-empty
                :description="searchKeyword ? $t('common.no_search_result') : $t('common.no_data')"
              />
            </div>
            <div v-else>
              <div v-for="item in newsList" :key="item.id" class="news-item">
                <div class="item-image" v-if="item.cover_image">
                  <el-image :src="item.cover_image" :alt="item.title" class="lazy-img" fit="cover">
                    <template #error>
                      <div class="image-viewer-slot image-slot item-image placeholder">
                        <div class="no-image"></div>
                      </div>
                    </template>
                  </el-image>
                </div>
                <!-- 无封面时占位置 -->
                <div class="item-image placeholder" v-else>
                  <div class="no-image"></div>
                </div>
                <div class="item-content">
                  <div
                    class="news-title-link"
                    @click="handleNewsClick(item)"
                    style="cursor: pointer;"
                  >
                    <h3>{{ item.title }}</h3>
                  </div>
                  <p class="summary" v-if="item.summary">{{ item.summary }}</p>
                  <div class="meta">
                    <span class="category">{{ item.category.name }}</span>
                    <span class="time">
                      {{ $t('pages.publish_time') }}: {{ formatTime(item.publish_time) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 分页：适配接口的 count/current 格式 -->
          <div v-if="!loading && totalPages > 1" class="pagination-wrapper">
            <el-pagination
              size="small"
              background
              layout="prev, pager, next"
              :total="totalCount"
              :page-size="pageSize"
              :current-page="currentPage"
              :page-count="totalPages"
              :disabled="loading"
              @current-change="handlePageChange"
            />
          </div>
        </el-col>

        <!-- 右侧：侧边栏（分类、热门） -->
        <el-col :span="6" :xs="24" class="sidebar-col">
          <div class="sidebar">
            <div class="sidebar-block">
              <h4>{{ $t('pages.news_list_title') }}</h4>
              <ul v-if="!categoryLoading">
                <!-- 全部分类选项 -->
                <li :class="{ active: currentCategory === null }">
                  <a href="#" @click.prevent="selectCategory(null)">{{ $t('common.all') }}</a>
                </li>
                <!-- 接口加载的分类 -->
                <li
                  v-for="cat in flatCategories"
                  :key="cat.id"
                  :class="{ active: cat.id === currentCategory }"
                >
                  <a href="#" @click.prevent="selectCategory(cat.id)">{{ cat.name }}</a>
                </li>
              </ul>
              <el-skeleton :rows="5" animated v-else />
            </div>
            <!--            <div class="sidebar-block">-->
            <!--              <h4>{{ $t('pages.hot_news') }}</h4>-->
            <!--              &lt;!&ndash; 预留热门新闻接口，暂时保留模拟数据，可后续替换 &ndash;&gt;-->
            <!--              <ul>-->
            <!--                <li v-for="hot in hotNews" :key="hot.id">-->
            <!--                  <router-link :to="`/news/${hot.id}`">{{ hot.title }}</router-link>-->
            <!--                </li>-->
            <!--              </ul>-->
            <!--            </div>-->
          </div>
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<script setup lang="ts">

import { ref, onMounted, computed, watch, nextTick, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { format } from 'date-fns';
import { Search } from '@element-plus/icons-vue';
import { getNewsListApi, getNewsCategoryApi } from '@/assets/api/website';
import { useRoute, useRouter } from 'vue-router';

// -------------------------- 类型定义 --------------------------
interface CategoryItem {
  id: number;
  name: string;
  pid: number;
  level: number;
  sort: number;
  children?: CategoryItem[];
}

interface NewsCategory {
  id: number;
  name: string;
}

interface NewsListItem {
  id: number;
  title: string;
  summary?: string;
  cover_image?: string;
  publish_time: string | Date;
  category: NewsCategory;
  content_len: number;
  external_link?: string;
}

// -------------------------- 状态管理 --------------------------
const { t, locale } = useI18n();
const router = useRouter();
const route = useRoute();

// 加载状态
const loading = ref(true);
const categoryLoading = ref(true);

// 新闻列表数据
const newsList = ref<NewsListItem[]>([]);
// 分页相关
const pageSize = ref(10);
const currentPage = ref(1);
const totalPages = ref(0);
const totalCount = ref(0);
const currentCategory = ref<number | null>(null);
const searchKeyword = ref('');

const defaultImage = ref(new URL('@/assets/img/default.jpg', import.meta.url).href)
// 分类数据
const categories = ref<CategoryItem[]>([]);
const flatCategories = computed(() => {
  const flatten = (list: CategoryItem[]): CategoryItem[] => {
    return list.reduce((acc, item) => {
      acc.push(item);
      if (item.children && item.children.length) {
        acc.push(...flatten(item.children));
      }
      return acc;
    }, [] as CategoryItem[]);
  };
  return flatten(categories.value);
});

// -------------------------- 状态存储与恢复 --------------------------
const STORAGE_KEY = 'news_list_state';

// 保存当前页面状态
const savePageState = () => {
  const state = {
    currentPage: currentPage.value,
    currentCategory: currentCategory.value,
    searchKeyword: searchKeyword.value,
    lang: locale.value
  };
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
};

// 恢复页面状态
const restorePageState = () => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      const state = JSON.parse(stored);
      if (state.lang === locale.value) {
        currentPage.value = state.currentPage || 1;
        currentCategory.value = state.currentCategory;
        searchKeyword.value = state.searchKeyword || '';
      }
    }
  } catch (e) {
    console.error('恢复新闻列表状态失败:', e);
  }
};

// 清除状态
const clearPageState = () => {
  sessionStorage.removeItem(STORAGE_KEY);
};

// 处理浏览器后退/前进
const handlePopState = () => {
  setTimeout(() => {
    if (route.path.includes('/news/list')) {
      restorePageState();
      fetchNewsList();
    }
  }, 100);
};

// -------------------------- 核心方法 --------------------------
const formatTime = (time: string | Date) => {
  if (!time) return '';
  try {
    return format(new Date(time), 'yyyy-MM-dd');
  } catch (e) {
    return time.toString();
  }
};

const fetchCategories = async () => {
  categoryLoading.value = true;
  try {
    const res = await getNewsCategoryApi({ lang: locale.value });
    categories.value = res as CategoryItem[];
    categories.value.sort((a, b) => a.id - b.id);
  } catch (error) {
    console.error('获取新闻分类失败:', error);
    categories.value = [];
  } finally {
    categoryLoading.value = false;
  }
};

const fetchNewsList = async () => {
  loading.value = true;
  try {
    const params: any = {
      lang: locale.value,
      page: currentPage.value,
      size: pageSize.value,
      category_id: currentCategory.value,
    };

    if (searchKeyword.value.trim()) {
      params.keyword = searchKeyword.value.trim();
    }

    const res = await getNewsListApi(params);
    newsList.value = res.list;
    totalPages.value = res.count;
    currentPage.value = res.current;
    totalCount.value = totalPages.value * pageSize.value;
  } catch (error) {
    console.error('获取新闻列表失败:', error);
    newsList.value = [];
    totalPages.value = 0;
    totalCount.value = 0;
  } finally {
    loading.value = false;
  }
};

const handlePageChange = (page: number) => {
  currentPage.value = page;
  savePageState();
  fetchNewsList();
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const selectCategory = (categoryId: number | null) => {
  currentCategory.value = categoryId;
  currentPage.value = 1;
  savePageState();
  fetchNewsList();
};

const handleNewsClick = (item: NewsListItem) => {
  // 跳转前先保存状态
  savePageState();
  if (item.content_len === 0 && item.external_link && item.external_link.trim() !== '') {
    window.open(item.external_link, '_blank');
  } else {
    router.push(`/news/${item.id}`);
  }
};

const handleSearch = () => {
  currentPage.value = 1;
  savePageState();
  fetchNewsList();
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const resetSearch = () => {
  searchKeyword.value = '';
  currentPage.value = 1;
  savePageState();
  fetchNewsList();
};

// -------------------------- 监听与初始化 --------------------------
// 监听路由变化
watch(
  () => route.fullPath,
  (newPath, oldPath) => {
    // 离开列表页时保存状态
    if (oldPath?.includes('/news/list') && !newPath.includes('/news/list')) {
      savePageState();
    }
    // 回到列表页时恢复状态
    if (newPath.includes('/news/list') && oldPath && !oldPath.includes('/news/list')) {
      restorePageState();
      nextTick(() => {
        fetchNewsList();
      });
    }
  },
  { immediate: false }
);

// 监听语言变化
watch(
  () => locale.value,
  () => {
    fetchCategories();
    // 语言切换时重置页码
    currentPage.value = 1;
    savePageState();
    fetchNewsList();
  },
  { immediate: true }
);

// 初始化
onMounted(() => {
  // 恢复状态（从详情页返回场景）
  const referrer = document.referrer;
  if (referrer && referrer.includes('/news/') && !referrer.includes('/news/list')) {
    restorePageState();
  }

  // fetchCategories();
  // fetchNewsList();

  // 监听浏览器后退/前进
  window.addEventListener('popstate', handlePopState);
});

onUnmounted(() => {
  window.removeEventListener('popstate', handlePopState);
});
</script>
<style scoped lang="scss">
@import '@/assets/css/common.scss';

.news-list-page {
  .page-header {
    padding: 100px 0 40px;
    /* 背景优化：渐变 + 背景图叠加 */
    background:
      linear-gradient(90deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%),
      url('@/assets/img/header.jpg') center center / cover no-repeat;
    /* 高斯模糊：使用伪元素实现（避免模糊影响内容），模糊值 2px（极轻微） */
    position: relative;
    border-bottom: 1px solid rgba($primary-color, 0.1);

    // 伪元素实现背景模糊（不影响内容）
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: inherit;
      filter: blur(2px);
      z-index: -1;
    }

    h1 {
      font-family: $font-serif;
      font-size: 2.5rem;
      font-weight: 700;
      color: $primary-color-2;
      margin-bottom: 20px;
      letter-spacing: 5px;
      /* 浅白色文字阴影：极淡、低模糊、小偏移，仅增强文字层次感 */
      text-shadow: 0 1px 2px rgba(255, 255, 255, 0.8);
      position: relative; // 确保文字在模糊层上方
    }

    .breadcrumb {
      font-size: 14px;
      color: #999;
      position: relative; // 确保面包屑在模糊层上方

      a {
        color: #666;
        text-decoration: none;

        &:hover {
          color: $primary-color;
        }
      }

      span {
        color: $primary-color;
      }
    }
  }

  .page-content {
    padding: 50px;
    @media (max-width: 768px) {
      padding: 20px;
    }
  }

  // 新增：搜索框样式
  .search-wrapper {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 20px;
    padding: 15px;
    background-color: #f9f9f9;
    border-radius: 8px;

    .el-input {
      flex: 1;
      max-width: 400px;
      border: 1px solid $primary-color;
    }
    :deep(.el-button) {
      padding: 10px 20px;
    }
    .search-btn {
      white-space: nowrap;
    }

    .reset-btn {
      color: #999;
      border: 1px solid $primary-color;
      &:hover {
        color: $primary-color;
      }
    }

    @media (max-width: 768px) {
      flex-wrap: wrap;

      .el-input {
        width: 100%;
        max-width: none;
        margin-bottom: 10px;
      }
    }
    @media (max-width: 768px) {
      flex-direction: column;
      align-items: stretch;
      padding: 10px;

      .el-input {
        width: 100%;
        max-width: none;
        margin-bottom: 10px;
      }

      // 新增按钮行容器
      .button-row {
        display: flex;
        gap: 10px;
      }

      .search-btn, .reset-btn {
        flex: 1;
        min-width: auto;
        width: calc(50% - 12px);
      }
    }
  }

  .list-wrapper {
    min-height: 500px;
  }

  .news-item {
    display: flex;
    margin-bottom: 30px;
    padding-bottom: 30px;
    border-bottom: 1px solid #eee;
    transition: box-shadow 0.3s;

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    }

    .item-image {
      flex-shrink: 0;
      width: 200px;
      height: 150px;
      overflow: hidden;
      border-radius: 8px;
      margin-right: 20px;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform 0.3s;
      }

      &.placeholder {
        background-color: #f5f5f5;
        display: flex;
        align-items: center;
        justify-content: center;

        .no-image {
          width: 100%;
          height: 100%;
          background: url("@/assets/img/default.jpg");
          background-size: cover;
          color: #999;
          font-size: 14px;
        }
      }
    }

    &:hover .item-image img {
      transform: scale(1.05);
    }

    .item-content {
      flex-grow: 1;

      h3 {
        font-size: 20px;
        margin-top: 0;
        margin-bottom: 10px;
        color: #333;
        transition: color 0.3s;
        letter-spacing: 1px;
        &:hover {
          color: $primary-color;
        }
      }

      .summary {
        color: #666;
        line-height: 1.6;
        margin-bottom: 15px;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }

      .meta {
        display: flex;
        gap: 20px;
        font-size: 13px;
        color: #999;

        .category {
          color: $primary-color;
          font-weight: bold;
        }
      }
    }
  }

  .pagination-wrapper {
    text-align: center;
    margin-top: 30px;
  }

  .sidebar {
    padding: 20px;
    background-color: #f9f9f9;
    border-radius: 8px;

    .sidebar-block {
      margin-bottom: 30px;

      h4 {
        font-size: 18px;
        color: $primary-color;
        border-bottom: 2px solid $primary-color;
        padding-bottom: 10px;
        margin-bottom: 15px;
      }

      ul {
        list-style: none;
        padding: 0;

        li {
          margin-bottom: 10px;
          font-size: 14px;

          a {
            color: #666;
            text-decoration: none;
            transition: color 0.3s;

            &:hover {
              color: $primary-color;
            }
          }

          &.active a {
            color: $primary-color;
            font-weight: bold;
          }
        }
      }
    }
  }

  // 响应式调整
  @media (max-width: 768px) {
    .news-item {
      flex-direction: column;

      .item-image {
        width: 100%;
        height: 230px;
        margin-right: 0;
        margin-bottom: 15px;
      }
    }

    .sidebar-col {
      margin-top: 30px;
    }
  }
}
</style>